import { DateTime } from 'luxon';
import * as crypto from 'crypto';
import { AdvancedMethodsRMTesting } from '@cf-shared/enums';
import { FitnessPlan, IntensityLevel, Plan_ExercisePath, Plan_Workout, getSlotPathIntensity } from '@cf-shared/data-model/fitness-plan';
import { setPlanSetCount } from '@cf-shared/data-model/plan-set-count';
import { ExerciseStat, FitnessState } from '@cf-shared/data-model/user-state';
import { CustomExercise, generateCustomExerciseId } from '@cf-shared/data-model/exercise';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { ExerciseDbService, ExerciseSlot } from 'services/exercise-db.service';
import { UserDb } from 'services/user-db.service';
import { addExerciseToWorkoutAsync } from 'services/exercise-switching';
import { ResolvedProgram, ResolvedProgramExercise, distributeRepTargetAcrossLevels, periodizationRebuildLevel } from './workout-plan-scanner-mapping';

// Apply a scanned workout program onto the edit-workouts DRAFT plan/state.
//
// CRITICAL: this module mutates the passed draft objects in place and does NOT
// persist the plan or state — edit-workouts saves both docs on its own Save
// (`onSaveClick` → `commitDraft` → `savePlanAsync` + `saveFitnessStateAsync`).
// That is what makes even "replace" reviewable and cancelable.
// ONE exception: auto-created CUSTOM EXERCISES are saved to the user DB
// immediately (see ensureCustomExerciseAsync below) — same as the picker's
// manual Create Exercise path; they are independent docs, not plan state.
//
// The pure review-rows → resolved-program mapping lives in
// workout-plan-scanner-mapping.ts (import-light so it is unit-testable).

export type WorkoutPlanApplyMode = 'append' | 'replace';

/**
 * Write the resolved program into the edit-workouts draft.
 *
 * - Replace first clears the TRAINING STRUCTURE only (`workouts` + `subSlots`);
 *   nutrition, body-comp, and every other plan field stay untouched.
 * - Each scanned day becomes a new `Plan_Workout`; each exercise goes through the
 *   production add-path (`addExerciseToWorkoutAsync`), which creates a fresh
 *   subslot or REUSES the shared one when the exercise is already in the plan —
 *   keeping the `subSlots` ↔ `subSlotIds` invariants intact.
 * - The add-path creates fresh subslots with `defaultVolume_sets = 1` and never
 *   touches a reused subslot's sets, so the scanned values are written explicitly
 *   afterwards. For a SHARED subslot this overwrites the one instance's sets/reps,
 *   changing every day that uses it — the owner-decided "update from scan" behavior
 *   the review screen warns about.
 * - `rebuildPathsForIntensity = Initial` makes the new sets/reps take effect on the
 *   next plan load (the solver is NOT run). Deliberate consequence, per the PRD's
 *   primary instruction: for an exercise the user is mid-progression on, the path
 *   rebuild resets its progression to the new target — "update from scan" semantics.
 *   Verified on real data at the PRD's on-device STOP.
 */
export async function applyScannedProgramToDraftAsync(
  program: ResolvedProgram,
  mode: WorkoutPlanApplyMode,
  draftPlan: FitnessPlan,
  draftState: FitnessState,
  exerciseDb: ExerciseDbService,
  userDb: UserDb,
  uid: string,
  todayLocal: DateTime,
  watcher: DocWatcher,
): Promise<void> {
  if (mode === 'replace') {
    // clear the training structure only — every other plan field is preserved
    draftPlan.workouts = [];
    draftPlan.subSlots = {};
  }

  // the same scanned custom name on several days must become ONE custom exercise
  // shared across them (mirrors how existing exercises share their subslot). Seeded
  // lazily from the user's EXISTING customs on first use, so a retried apply after a
  // mid-run failure (or a pre-existing same-named custom) reuses the doc instead of
  // creating a duplicate.
  const createdCustomIdByName = new Map<string, string>();
  let customSeedDone = false;
  const seedExistingCustomsAsync = async () => {
    if (customSeedDone) return;
    customSeedDone = true;
    const existing = await userDb.getAllCustomExercisesAsync();
    for (const custom of existing) {
      if (custom.virtuallyDeleted || !custom.name) continue;
      const key = custom.name.trim().toLocaleLowerCase();
      if (!createdCustomIdByName.has(key)) {
        createdCustomIdByName.set(key, custom._id);
      }
    }
  };

  // lazy slot lookup — a created custom is assigned to its similar exercise's slot so
  // the edit-workouts "Change exercise" picker filters to that slot. Without this a
  // slotless custom lands in the generic $custom slot and the picker shows ALL
  // exercises. Exact match only, NO first-slot fallback (mirrors the scanner page's
  // slotIdForExerciseAsync). Slots load once, only if a similar-backed custom needs one.
  let allSlotsCache: ExerciseSlot[] | undefined;
  const slotIdForSimilarExerciseAsync = async (similarId: string): Promise<string | undefined> => {
    if (!allSlotsCache) {
      allSlotsCache = await exerciseDb.getAllSlotsAsync(watcher);
    }
    return allSlotsCache.find(s => s.exercises?.some(e => e.exerciseId === similarId))?._id;
  };

  // workout names follow the app's A/B/C… convention (same as the solver-generated
  // plans in sv-plan-handler), NOT the scanned day labels — the scanned name is only
  // shown on the review screen. On append, continue with the first unused letter.
  const usedNames = new Set(draftPlan.workouts.map(w => w.name));
  const nextUnusedLetter = (): string => {
    for (let i = 0; i < 26; i++) {
      const letter = String.fromCharCode(65 + i);
      if (!usedNames.has(letter)) {
        usedNames.add(letter);
        return letter;
      }
    }
    // more than 26 workouts is beyond any real plan; fall back to a numbered name,
    // recording it so a second overflow day cannot reuse the same name
    const fallback = `W${usedNames.size + 1}`;
    usedNames.add(fallback);
    return fallback;
  };

  for (const day of program.days) {
    const workout = new Plan_Workout();
    workout.name = nextUnusedLetter();
    workout.subSlotIds = [];
    draftPlan.workouts.push(workout);
    const workoutIndex = draftPlan.workouts.length - 1;

    for (const exercise of day.exercises) {
      if (!exercise.exerciseId) {
        await seedExistingCustomsAsync();
      }
      const exerciseId = exercise.exerciseId
        ?? await ensureCustomExerciseAsync(exercise, createdCustomIdByName, userDb, uid, slotIdForSimilarExerciseAsync);

      // production add-path: fresh subslot for a new exercise, shared subslot when
      // the exercise already exists anywhere in the (draft) plan
      const { newSubslotId } = await addExerciseToWorkoutAsync(
        workoutIndex,
        exerciseId,
        draftPlan, draftState, exerciseDb, userDb, todayLocal, watcher,
      );

      const subSlot = draftPlan.subSlots[newSubslotId];
      // the scanned count is the user's own number, so it is written the way the exercise
      // Settings page writes one: as the base, clearing any adjustment the add-path seeded
      // from history (#6665, design-docs/volume-autoregulation.md)
      setPlanSetCount(subSlot, exercise.sets);

      // the intensity levels this exercise actually uses: a shared/history-restored
      // subslot keeps its DUP (heavy+light) or TUP (heavy+medium+light) paths; a
      // brand-new one has none → a single non-periodized Initial level.
      const activeLevels = subSlot.exercisePaths?.length
        ? subSlot.exercisePaths.map(p => p.intensityLevel)
        : [IntensityLevel.Initial];

      if (exercise.targetReps !== undefined) {
        // seed the per-exercise rep preference. The add-path does NOT initialize
        // exerciseStats for a brand-new exercise, so lazy-init the stat record first
        // (same `|| {} as ExerciseStat` precedent as exercise-configuration-page)
        const stat = draftState.exerciseStats[exerciseId] || {} as ExerciseStat;
        stat.preferRepTargetByIntensity ||= {};
        // ONE scanned target, but DUP/TUP needs one per session: anchor it to the
        // closest level and shift the others by the same offset so spacing holds
        // (owner decision 2026-06-13 — a fixed-to-heavy write produced e.g. heavy 10
        // next to light 13, violating the ≥4-apart rule). Non-periodized → just Initial.
        const distributed = distributeRepTargetAcrossLevels(
          activeLevels.map(level => ({
            intensityLevel: level,
            // getSlotPathIntensity only reads intensityLevel; the cast supplies the
            // unused required fields (same pattern as exercise-configuration-page)
            intensityPct: getSlotPathIntensity(subSlot, { intensityLevel: level } as Plan_ExercisePath),
          })),
          exercise.targetReps,
        );
        for (const { intensityLevel, targetReps } of distributed) {
          stat.preferRepTargetByIntensity[intensityLevel] = targetReps;
        }
        // a STATED target means skip RM testing and use that target directly. Without
        // this the exercise stays in RM-testing mode (exerciseUsesRMTesting defaults to
        // true) and the rep target shows as "determined after RM testing" instead of the
        // scanned value. Rows WITHOUT a scanned target leave preferRMTesting unset → RM
        // testing per the user's general setting.
        stat.preferRMTesting = AdvancedMethodsRMTesting.Avoid;
        draftState.exerciseStats[exerciseId] = stat;
      }

      // rebuild paths on the next plan load, PRESERVING the exercise's periodization —
      // a forced Initial here would collapse an already-DUP/TUP exercise to a single
      // path (losing the heavy/light split the distributed targets were computed for).
      subSlot.rebuildPathsForIntensity = periodizationRebuildLevel(activeLevels);
    }
  }
}

/**
 * Create (or reuse, within this apply run) the custom exercise for a create-custom
 * entry and return its id.
 *
 * NOTE on persistence: unlike everything else in this module, saving a custom
 * exercise writes to the user DB IMMEDIATELY — the same behavior as the picker's
 * manual "Create Exercise" path. A custom exercise is an independent document, not
 * part of the plan draft; if the user later cancels edit-workouts, the created
 * customs remain in "My Exercises" (harmless, reusable, deletable).
 *
 * The created doc mirrors the edit-exercise create-mode shape, plus: the rejected
 * suggestion as `similarExerciseId` (the edit-exercise "similar exercise" property,
 * used for muscle targeting), and — when that similar exercise has a slot —
 * `usedInSlots` set to it, so the add-path files the custom under a REAL slot (not
 * the generic $custom) and a later "Change exercise" picker filters to that slot.
 * A custom with no similar exercise has no basis for a slot and stays slotless (the
 * picker then shows all exercises, same as a bare edit-exercise custom).
 */
async function ensureCustomExerciseAsync(
  exercise: ResolvedProgramExercise,
  createdCustomIdByName: Map<string, string>,
  userDb: UserDb,
  uid: string,
  slotIdForSimilarExerciseAsync: (similarId: string) => Promise<string | undefined>,
): Promise<string> {
  const spec = exercise.createCustom;
  // key the same way seedExistingCustomsAsync does (trim + lowercase) so this lookup
  // and the seeded existing-customs entries share one key space and dedup correctly
  const nameKey = spec.name.trim().toLocaleLowerCase();
  const existing = createdCustomIdByName.get(nameKey);
  if (existing) return existing;

  const custom: CustomExercise = {
    usedInSlots: [],
    name: spec.name,
    shortname: '',
    description: '',
    bw_pc: 0,
    allowMyoReps: true,
    _id: '',
  } as CustomExercise;
  if (spec.similarExerciseId) {
    custom.similarExerciseId = spec.similarExerciseId;
    // give the custom the similar exercise's slot so it belongs to a real muscle slot
    const slotId = await slotIdForSimilarExerciseAsync(spec.similarExerciseId);
    if (slotId) custom.usedInSlots = [slotId];
  }
  generateCustomExerciseId(uid, custom, crypto);
  await userDb.saveCustomExerciseAsync(custom, DatabaseWrapper.getTransactionId());

  createdCustomIdByName.set(nameKey, custom._id);
  return custom._id;
}
